import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../config';
import PanelHelpBadge from './PanelHelpBadge';

const getAdminHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CONFIDENCE_STYLE = {
  HIGH: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40',
  MEDIUM: 'bg-amber-500/20 text-amber-300 border border-amber-500/40',
  LOW: 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
};

const HELP_BY_PANEL = {
  overview: {
    title: 'Urun Analizi',
    description: 'Bu sayfa davranis, tercih, negatif sinyal ve aksiyon akislarini tek noktadan izlemek icindir.',
    interpretation: 'Filtreyi degistirdiginde tum paneller ayni zaman penceresine gore guncellenir.',
    metricExample: 'Oran %20den %35e cikiyorsa artan etki var demektir.'
  },
  why: {
    title: 'Neden / Kok Sebep',
    description: 'Kullanici davranisindaki dusus nedenlerini dagilim halinde gosterir.',
    interpretation: 'Orani yuksek nedenler iyilestirme onceligidir.',
    metricExample: 'Bir neden %12den %28e ciktiysa o alanda sorun buyuyor olabilir.'
  },
  experiments: {
    title: 'Deney / A-B',
    description: 'Varyantlarin donusum ve kayip farklarini karsilastirir.',
    interpretation: 'Dusuk orneklemde farklar gecici olabilir.',
    metricExample: 'Donusum farki %3ten kucukse once orneklem buyuklugune bak.'
  },
  journeys: {
    title: 'Kullanici Yolculugu',
    description: 'En cok izlenen yollari ve terk adimlarini gosterir.',
    interpretation: 'Terk yogun adimlar urun aksiyonuna donusturulmelidir.',
    metricExample: 'Plan adiminda terk %40 ise plan ekrani sadeleştirme adayi olabilir.'
  },
  favorites: {
    title: 'Favori Zekasi',
    description: 'Favori sinyallerinin kullanim davranisina etkisini gosterir.',
    interpretation: 'Favori-kullanim farki buyurse onerilerde uyum sorunu vardir.',
    metricExample: 'Favori->kullanim farki %10un ustunde ise eslesme kurali gozden gecirilir.'
  },
  ingredient: {
    title: 'Malzeme Egilimi',
    description: 'Kullanici bazli malzeme tercihlerini ve guclu sinyalleri gosterir.',
    interpretation: 'Temel/staple malzemeler asiri agirlik almamalidir.',
    metricExample: 'Temel malzeme orani cok yuksekse affinity yorumunda gultu artar.'
  },
  shadow: {
    title: 'Mutfak Karar Golge',
    description: 'Karar profilinin canliya etki etmeden ogrenim performansini izler.',
    interpretation: 'Shadow modunda canli siralama degismemelidir.',
    metricExample: 'Top1 ortusme cok dusukse warmup once analiz edilmelidir.'
  },
  negatives: {
    title: 'Negatif Sinyaller',
    description: 'Kullaniciyi zorlayan davranislari oran bazli takip eder.',
    interpretation: 'Orani yukselen sinyaller urun deneyiminde surtunme oldugunu gosterir.',
    metricExample: 'Ofkeli tiklama orani artiyorsa ayni adimda tekrarli deneme vardir.'
  },
  realtime: {
    title: 'Canli Mini',
    description: 'Anlik aktif kullanici ve olay akisinin kisa gorunumudur.',
    interpretation: 'Sistemin o anda veri uretip uretmedigini hizli kontrol etmeni saglar.',
    metricExample: 'Aktif oturum var ama olay yoksa ingest gecikmesi kontrol edilmelidir.'
  },
  insights: {
    title: 'Otomatik Icgoruler',
    description: 'Kural tabanli uretilen icgoruleri ve onceliklerini listeler.',
    interpretation: 'Aksiyon Gerekli etiketli icgoruler once ele alinmalidir.',
    metricExample: 'Ayni icgoru tekrar ediyorsa kural eşiği yeniden ayarlanabilir.'
  },
  aiNarrative: {
    title: 'YZ Anlatimi',
    description: 'Icgoruleri insan dilinde aciklar, aksiyon adayi onerir.',
    interpretation: 'Karar motorunu degistirmez; karar destek katmani olarak calisir.',
    metricExample: 'Guven dusukse oneriyi dogrudan yayina almak yerine insan onayi gerekir.'
  },
  actionQueue: {
    title: 'Aksiyon Sirasi',
    description: 'Acik aksiyon maddelerinin operasyon durumlarini takip eder.',
    interpretation: 'Onaylandi -> Suruyor -> Cozuldu akisiyla calisir.',
    metricExample: 'Uzun sure acik kalan aksiyonlar uygulama hizinda darbogaz oldugunu gosterir.'
  }
};

function ConfidenceBadge({ meta }) {
  const confidence = String(meta?.confidence || 'LOW').toUpperCase();
  const sampleSize = Number(meta?.sampleSize || 0);
  const window = String(meta?.window || 'default');
  const cls = CONFIDENCE_STYLE[confidence] || CONFIDENCE_STYLE.LOW;
  return (
    <span className={`inline-flex items-center gap-2 rounded-full px-2 py-1 text-[11px] font-bold ${cls}`}>
      {confidence}
      <span className="opacity-80">n={sampleSize}</span>
      <span className="opacity-80">{window}</span>
    </span>
  );
}

function PanelCard({ title, meta, children, emptyText, help }) {
  const lowSample = Boolean(meta?.lowSample);
  return (
    <section className="rounded-xl border border-slate-800 bg-[#101922] p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-bold text-white">{title}</h3>
          {help ? (
            <PanelHelpBadge
              title={help.title || title}
              description={help.description || ''}
              interpretation={help.interpretation || ''}
              metricExample={help.metricExample || ''}
            />
          ) : null}
        </div>
        <ConfidenceBadge meta={meta} />
      </div>
      {lowSample ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
          Henuz yeterli veri yok. Veri toplanmaya devam ediyor...
        </div>
      ) : (
        children || (
          <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-3 text-sm text-slate-400">
            {emptyText || 'Veri yok'}
          </div>
        )
      )}
    </section>
  );
}

function NumberCell({ value }) {
  return <span className="font-semibold text-white">{Number(value || 0).toLocaleString('tr-TR')}</span>;
}

function RateCell({ value }) {
  return <span className="font-semibold text-cyan-300">{Number(value || 0).toLocaleString('tr-TR')}%</span>;
}

function aksiyonDurumEtiketi(status) {
  const key = String(status || '').toLowerCase();
  if (key === 'open') return 'acik';
  if (key === 'acknowledged') return 'onaylandi';
  if (key === 'in_progress') return 'suruyor';
  if (key === 'resolved') return 'cozuldu';
  if (key === 'dismissed') return 'kapatildi';
  return key || '-';
}

function trendEtiketi(direction) {
  const key = String(direction || '').toLowerCase();
  if (key === 'up') return 'yukselis';
  if (key === 'down') return 'dusus';
  if (key === 'stable') return 'dengeli';
  return key || '-';
}

function HorizontalBars({ items, valueKey = 'value', labelKey = 'label', colorClass = 'bg-cyan-500' }) {
  const maxValue = Math.max(1, ...items.map((i) => Number(i?.[valueKey] || 0)));
  return (
    <div className="space-y-2">
      {items.map((item) => {
        const val = Number(item?.[valueKey] || 0);
        const pct = Math.max(0, Math.min(100, (val / maxValue) * 100));
        return (
          <div key={`${item?.[labelKey]}-${val}`}>
            <div className="mb-1 flex items-center justify-between text-xs text-slate-300">
              <span className="truncate pr-3">{item?.[labelKey]}</span>
              <span>{val.toLocaleString('tr-TR')}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-800">
              <div className={`h-2 rounded-full ${colorClass}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ProductAnalyticsPanel({ embedded = false }) {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    window: 'default',
    source: '',
    surface: '',
    context: ''
  });
  const [data, setData] = useState({
    why: null,
    experiments: null,
    journeys: null,
    negatives: null,
    realtime: null,
    insights: null,
    favoritesIntel: null,
    favoritesExplainability: null,
    favoritesMetrics: null,
    ingredientAffinity: null,
    kitchenDecisionShadow: null
  });
  const [aiNarrative, setAiNarrative] = useState(null);
  const [aiNarrativeDisabled, setAiNarrativeDisabled] = useState(false);
  const [aiNarrativeForbidden, setAiNarrativeForbidden] = useState(false);
  const [aiCanCreateActionItems, setAiCanCreateActionItems] = useState(false);
  const [aiActionBusyTopic, setAiActionBusyTopic] = useState('');
  const [aiActionError, setAiActionError] = useState('');
  const [actionQueue, setActionQueue] = useState([]);
  const [actionQueueLoading, setActionQueueLoading] = useState(false);
  const [actionQueueError, setActionQueueError] = useState('');
  const [narrativeFeedbackBusy, setNarrativeFeedbackBusy] = useState('');
  const [narrativeFeedbackError, setNarrativeFeedbackError] = useState('');
  const [preferenceFeedbackBusy, setPreferenceFeedbackBusy] = useState('');
  const [preferenceFeedbackError, setPreferenceFeedbackError] = useState('');
  const [preferenceFeedbackSummary, setPreferenceFeedbackSummary] = useState(null);
  const [rollout, setRollout] = useState({
    loaded: false,
    stage: 'full',
    allowed: true,
    reason: 'full'
  });
  const [shadowDays, setShadowDays] = useState(30);

  const queryString = useMemo(() => {
    const q = new URLSearchParams();
    q.set('window', filters.window);
    if (filters.source) q.set('source', filters.source);
    if (filters.surface) q.set('surface', filters.surface);
    if (filters.context) q.set('context', filters.context);
    return q.toString();
  }, [filters]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const headers = getAdminHeaders();
        const rolloutRes = await fetch(`${API_BASE_URL}/api/admin/analytics/v3-rollout-status`, { credentials: 'include', headers });
        const rolloutJson = rolloutRes.ok ? await rolloutRes.json() : null;
        const allowed = Boolean(rolloutJson?.access?.allowed);
        const stage = String(rolloutJson?.rollout?.stage || 'full');
        const reason = String(rolloutJson?.access?.reason || 'unknown');

        if (!mounted) return;
        setRollout({
          loaded: true,
          stage,
          allowed,
          reason,
          stagedPercent: Number(rolloutJson?.rollout?.stagedPercent || 0)
        });

        if (!allowed) {
          setData({
            why: null,
            experiments: null,
            journeys: null,
            negatives: null,
            realtime: null,
            insights: null,
            favoritesIntel: null,
            favoritesExplainability: null,
            favoritesMetrics: null,
            ingredientAffinity: null,
            kitchenDecisionShadow: null
          });
          setAiNarrative(null);
          setAiNarrativeDisabled(false);
          setAiNarrativeForbidden(false);
          setAiCanCreateActionItems(false);
          return;
        }

        const [whyRes, expRes, journeyRes, negRes, rtRes, insRes, favRes, favExplainRes, favMetricsRes, ingredientAffinityRes, kitchenShadowRes, aiNarrativeRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/admin/analytics/why-root-cause?${queryString}`, { credentials: 'include', headers }),
          fetch(`${API_BASE_URL}/api/admin/analytics/experiments?${queryString}`, { credentials: 'include', headers }),
          fetch(`${API_BASE_URL}/api/admin/analytics/journey-paths?${queryString}`, { credentials: 'include', headers }),
          fetch(`${API_BASE_URL}/api/admin/analytics/negative-signals?${queryString}`, { credentials: 'include', headers }),
          fetch(`${API_BASE_URL}/api/admin/analytics/realtime-mini?${queryString}`, { credentials: 'include', headers }),
          fetch(`${API_BASE_URL}/api/admin/analytics/insights?${queryString}`, { credentials: 'include', headers }),
          fetch(`${API_BASE_URL}/api/admin/analytics/favorites-intelligence?${queryString}`, { credentials: 'include', headers }),
          fetch(`${API_BASE_URL}/api/admin/favorites/explainability?entityType=recipe&limit=12`, { credentials: 'include', headers }),
          fetch(`${API_BASE_URL}/api/admin/favorites/metrics?days=30`, { credentials: 'include', headers }),
          fetch(`${API_BASE_URL}/api/admin/analytics/ingredient-affinity?${queryString}`, { credentials: 'include', headers }),
          fetch(`${API_BASE_URL}/api/kitchen/admin/metrics?days=${shadowDays}`, { credentials: 'include', headers }),
          fetch(`${API_BASE_URL}/api/admin/analytics/ai-narratives?${queryString}`, { credentials: 'include', headers })
        ]);

        if (!mounted) return;
        const [why, experiments, journeys, negatives, realtime, insights, favoritesIntel, favoritesExplainability, favoritesMetrics, ingredientAffinity, kitchenDecisionShadow] = await Promise.all([
          whyRes.ok ? whyRes.json() : null,
          expRes.ok ? expRes.json() : null,
          journeyRes.ok ? journeyRes.json() : null,
          negRes.ok ? negRes.json() : null,
          rtRes.ok ? rtRes.json() : null,
          insRes.ok ? insRes.json() : null,
          favRes.ok ? favRes.json() : null,
          favExplainRes.ok ? favExplainRes.json() : null,
          favMetricsRes.ok ? favMetricsRes.json() : null,
          ingredientAffinityRes.ok ? ingredientAffinityRes.json() : null,
          kitchenShadowRes.ok ? kitchenShadowRes.json() : null
        ]);

        setData({ why, experiments, journeys, negatives, realtime, insights, favoritesIntel, favoritesExplainability, favoritesMetrics, ingredientAffinity, kitchenDecisionShadow });
        setPreferenceFeedbackSummary(insights?.preferenceInsightQuality || null);

        if (aiNarrativeRes.status === 503) {
          const aiJson = await aiNarrativeRes.json().catch(() => ({}));
          if (aiJson?.error === 'analytics_ai_narrative_disabled') {
            setAiNarrativeDisabled(true);
            setAiNarrative(null);
            setAiNarrativeForbidden(false);
            setAiCanCreateActionItems(false);
          } else {
            setAiNarrativeDisabled(false);
            setAiNarrative(null);
            setAiNarrativeForbidden(false);
            setAiCanCreateActionItems(false);
          }
        } else if (aiNarrativeRes.status === 403) {
          setAiNarrativeForbidden(true);
          setAiNarrativeDisabled(false);
          setAiNarrative(null);
          setAiCanCreateActionItems(false);
        } else if (aiNarrativeRes.ok) {
          const aiJson = await aiNarrativeRes.json();
          setAiNarrativeForbidden(false);
          setAiNarrativeDisabled(false);
          setAiNarrative(aiJson);
          setAiCanCreateActionItems(Boolean(aiJson?.permissions?.canCreateAiActionItems));
        } else {
          setAiNarrative(null);
          setAiNarrativeForbidden(false);
          setAiCanCreateActionItems(false);
        }
      } catch (err) {
        console.error('Admin Urun Analizi fetch failed', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [queryString, shadowDays]);

  useEffect(() => {
    let mounted = true;
    const loadQueue = async () => {
      if (aiNarrativeForbidden || aiNarrativeDisabled) {
        if (mounted) setActionQueue([]);
        return;
      }
      try {
        setActionQueueLoading(true);
        setActionQueueError('');
        const headers = getAdminHeaders();
        const res = await fetch(`${API_BASE_URL}/api/admin/analytics/action-items`, {
          credentials: 'include',
          headers
        });
        if (!mounted) return;
        if (res.status === 403 || res.status === 503) {
          setActionQueue([]);
          return;
        }
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json?.success) {
          setActionQueueError(json?.error || 'action_queue_fetch_failed');
          setActionQueue([]);
          return;
        }
        setActionQueue(Array.isArray(json.items) ? json.items : []);
      } catch (error) {
        console.error('Aksiyon Sirasi fetch failed', error);
        if (mounted) setActionQueueError('action_queue_fetch_failed');
      } finally {
        if (mounted) setActionQueueLoading(false);
      }
    };
    loadQueue();
    return () => {
      mounted = false;
    };
  }, [aiNarrativeForbidden, aiNarrativeDisabled]);

  const whyRows = data.why?.rootCause?.reasons || [];
  const experimentRows = data.experiments?.experiments || [];
  const pathRows = data.journeys?.paths || [];
  const favoriteTopRows = data.favoritesIntel?.favorites?.topFavorited || [];
  const frequentTopRows = data.favoritesIntel?.favorites?.topFrequent || [];
  const conversionGapRows = data.favoritesIntel?.favorites?.conversionGap || [];
  const favoritesSegmentBreakdown = data.favoritesIntel?.favorites?.segmentBreakdown || null;
  const favoritesAnalysisOutputs = data.favoritesIntel?.favorites?.analysisOutputs || null;
  const explainRows = data.favoritesExplainability?.items || [];
  const favoriteMetrics = data.favoritesMetrics?.metrics || null;
  const topUsedIngredientRows = data.ingredientAffinity?.ingredientAffinity?.topUsedIngredients || [];
  const topAffinityIngredientRows = data.ingredientAffinity?.ingredientAffinity?.topAffinityIngredients || [];
  const ingredientBreakdown = data.ingredientAffinity?.ingredientAffinity?.stapleVsPreferenceBreakdown || null;
  const preferenceCoverage = data.ingredientAffinity?.ingredientAffinity?.preferenceCoverage || null;
  const ingredientHardRules = data.ingredientAffinity?.ingredientAffinity?.hardRules || null;
  const kitchenShadowHealth = data.kitchenDecisionShadow?.health || null;
  const kitchenShadowMetrics = data.kitchenDecisionShadow?.metrics || null;
  const kitchenShadowDiagnostics = data.kitchenDecisionShadow?.diagnostics || null;
  const signals = data.negatives?.signals || null;
  const realtime = data.realtime?.realtime || null;
  const insights = data.insights?.insights || [];
  const aiNarratives = aiNarrative?.narratives || [];
  const noActionRate = Number(signals?.rates?.noActionRate || 0);
  const rageRate = Number(signals?.rates?.rageClickRate || 0);
  const repeatRate = Number(signals?.rates?.repeatSearchRate || 0);
  const rapidRate = Number(signals?.rates?.rapidBackRate || 0);

  const updateActionQueueLocal = (nextItem) => {
    setActionQueue((prev) => {
      const arr = Array.isArray(prev) ? prev.slice() : [];
      const idx = arr.findIndex((x) => x.id === nextItem.id);
      if (idx >= 0) arr[idx] = nextItem;
      else arr.unshift(nextItem);
      return arr;
    });
  };

  const upsertLocalNarrativeAction = (topic, actionItem) => {
    setAiNarrative((prev) => {
      if (!prev?.narratives) return prev;
      return {
        ...prev,
        narratives: prev.narratives.map((n) => (
          n.topic === topic
            ? { ...n, actionItem: actionItem ? { id: actionItem.id, status: actionItem.status, createdAt: actionItem.createdAt } : null }
            : n
        ))
      };
    });
  };

  const submitNarrativeAction = async (narrative, status = 'open') => {
    try {
      setAiActionBusyTopic(narrative.topic);
      setAiActionError('');
      const headers = {
        ...getAdminHeaders(),
        'Content-Type': 'application/json'
      };
      const res = await fetch(`${API_BASE_URL}/api/admin/analytics/action-items`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          sourceInsightId: narrative.insightId,
          topic: narrative.topic,
          title: narrative.recommendedAction || narrative.whatHappened || narrative.topic,
          description: `${narrative.whatHappened}\n${narrative.whyLikely || ''}`.trim(),
          priority: narrative.impact || 'medium',
          confidence: narrative.confidence || 'low',
          impact: narrative.impact || 'medium',
          sampleSize: Number(narrative.sampleSize || 0),
          timeWindow: narrative.timeWindow || filters.window,
          dedupeKey: narrative.dedupeKey,
          recommendedAction: narrative.recommendedAction || narrative.topic,
          guardrails: narrative.guardrails || {},
          status
        })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) {
        setAiActionError(json?.error || 'action_submit_failed');
        return;
      }
      upsertLocalNarrativeAction(narrative.topic, json.item);
      if (json.item) updateActionQueueLocal(json.item);
    } catch (error) {
      console.error('AI action submit failed', error);
      setAiActionError('action_submit_failed');
    } finally {
      setAiActionBusyTopic('');
    }
  };

  const submitNarrativeFeedback = async (narrative, useful) => {
    try {
      setNarrativeFeedbackBusy(narrative.insightId || narrative.topic);
      setNarrativeFeedbackError('');
      const headers = {
        ...getAdminHeaders(),
        'Content-Type': 'application/json'
      };
      const res = await fetch(`${API_BASE_URL}/api/admin/analytics/ai-narratives/feedback`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          insightId: narrative.insightId,
          dedupeKey: narrative.dedupeKey,
          topic: narrative.topic,
          useful: Boolean(useful),
          sampleSize: Number(narrative.sampleSize || 0),
          confidence: narrative.confidence || 'low',
          timeWindow: narrative.timeWindow || filters.window
        })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) {
        setNarrativeFeedbackError(json?.error || 'narrative_feedback_submit_failed');
        return;
      }
    } catch (error) {
      console.error('Narrative feedback submit failed', error);
      setNarrativeFeedbackError('narrative_feedback_submit_failed');
    } finally {
      setNarrativeFeedbackBusy('');
    }
  };

  const submitPreferenceInsightFeedback = async (insight, useful) => {
    try {
      setPreferenceFeedbackBusy(String(insight?.key || ''));
      setPreferenceFeedbackError('');
      const headers = {
        ...getAdminHeaders(),
        'Content-Type': 'application/json'
      };
      const res = await fetch(`${API_BASE_URL}/api/admin/analytics/preference-insights/feedback`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          insightKey: insight?.key,
          useful: Boolean(useful),
          sampleSize: Number(insight?.sampleSize || 0),
          confidence: String(insight?.confidence || 'low').toLowerCase(),
          timeWindow: filters.window
        })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) {
        setPreferenceFeedbackError(json?.error || 'preference_insight_feedback_submit_failed');
        return;
      }

      const summaryRes = await fetch(`${API_BASE_URL}/api/admin/analytics/preference-insights/feedback-summary`, {
        credentials: 'include',
        headers: getAdminHeaders()
      });
      const summaryJson = await summaryRes.json().catch(() => ({}));
      if (summaryRes.ok && summaryJson?.success) {
        setPreferenceFeedbackSummary(summaryJson.summary || null);
      }
    } catch (error) {
      console.error('Preference insight feedback submit failed', error);
      setPreferenceFeedbackError('preference_insight_feedback_submit_failed');
    } finally {
      setPreferenceFeedbackBusy('');
    }
  };

  const patchActionItemStatus = async (itemId, status) => {
    try {
      setActionQueueError('');
      const headers = {
        ...getAdminHeaders(),
        'Content-Type': 'application/json'
      };
      const res = await fetch(`${API_BASE_URL}/api/admin/analytics/action-items/${itemId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers,
        body: JSON.stringify({ status })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success || !json?.item) {
        setActionQueueError(json?.error || 'action_queue_patch_failed');
        return;
      }
      updateActionQueueLocal(json.item);
      if (json.item?.topic) upsertLocalNarrativeAction(json.item.topic, json.item);
    } catch (error) {
      console.error('Aksiyon Sirasi patch failed', error);
      setActionQueueError('action_queue_patch_failed');
    }
  };

  const formatAge = (createdAt) => {
    if (!createdAt) return '-';
    const ms = Date.now() - new Date(createdAt).getTime();
    const h = Math.floor(ms / (1000 * 60 * 60));
    if (h < 24) return `${Math.max(0, h)}s`;
    const d = Math.floor(h / 24);
    return `${d}g`;
  };

  return (
    <div className={`mx-auto max-w-[1400px] space-y-5 ${embedded ? '' : 'pb-12'}`}>
      <div className="rounded-xl border border-slate-800 bg-[#101922] p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-black text-white">Urun Analizi</h2>
            <PanelHelpBadge
              title={HELP_BY_PANEL.overview.title}
              description={HELP_BY_PANEL.overview.description}
              interpretation={HELP_BY_PANEL.overview.interpretation}
            />
          </div>
          {loading ? <span className="text-sm text-slate-400">Yukleniyor...</span> : null}
        </div>
        {rollout.loaded && !rollout.allowed ? (
          <div className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
            V3 paneli su anda `{rollout.stage}` rollout modunda. Bu hesap icin erisim kapali (`{rollout.reason}`).
          </div>
        ) : null}
        {rollout.loaded && rollout.allowed && rollout.stage !== 'full' ? (
          <div className="mb-3 rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-sm text-cyan-200">
            V3 rollout asamasi: `{rollout.stage}`{rollout.stage === 'staged' ? ` (%${rollout.stagedPercent})` : ''}.
          </div>
        ) : null}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <select
            value={filters.window}
            onChange={(e) => setFilters((prev) => ({ ...prev, window: e.target.value }))}
            className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
          >
            <option value="short">24s</option>
            <option value="default">Son 7 gun</option>
            <option value="long">30g</option>
          </select>
          <input
            value={filters.source}
            onChange={(e) => setFilters((prev) => ({ ...prev, source: e.target.value }))}
            placeholder="source"
            className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
          />
          <input
            value={filters.surface}
            onChange={(e) => setFilters((prev) => ({ ...prev, surface: e.target.value }))}
            placeholder="surface"
            className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
          />
          <input
            value={filters.context}
            onChange={(e) => setFilters((prev) => ({ ...prev, context: e.target.value }))}
            placeholder="context"
            className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PanelCard title="Neden / Kok Sebep" meta={data.why?.meta} emptyText="Kok sebep verisi yok" help={HELP_BY_PANEL.why}>
          <HorizontalBars
            items={whyRows.map((r) => ({ label: r.reason, value: Number(r.count || 0) }))}
            valueKey="value"
            labelKey="label"
            colorClass="bg-amber-500"
          />
        </PanelCard>

        <PanelCard title="Deney / A-B" meta={data.experiments?.meta} emptyText="Deney verisi yok" help={HELP_BY_PANEL.experiments}>
          <div className="space-y-3">
            {experimentRows.length === 0 ? (
              <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-3 text-sm text-slate-400">Not enough data yet. Collecting data...</div>
            ) : (
              experimentRows.slice(0, 5).map((row) => (
                <div key={`${row.experimentId}-${row.variant}`} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <div className="mb-2 flex items-center justify-between text-xs text-slate-300">
                    <span>{row.experimentId} / {row.variant}</span>
                    <span>V:{row.premiumViewed} C:{row.premiumClicked} P:{row.premiumPaid}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="mb-1 text-[11px] text-slate-400">View -> Click</p>
                      <div className="h-2 rounded-full bg-slate-800"><div className="h-2 rounded-full bg-cyan-500" style={{ width: `${Number(row.premiumViewToClickRate || 0)}%` }} /></div>
                    </div>
                    <div>
                      <p className="mb-1 text-[11px] text-slate-400">Click -> Pay</p>
                      <div className="h-2 rounded-full bg-slate-800"><div className="h-2 rounded-full bg-emerald-500" style={{ width: `${Number(row.premiumClickToPayRate || 0)}%` }} /></div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </PanelCard>

        <PanelCard title="Kullanici Yolculugu" meta={data.journeys?.meta} emptyText="Yolculuk verisi yok" help={HELP_BY_PANEL.journeys}>
          <HorizontalBars
            items={pathRows.slice(0, 8).map((r) => ({ label: r.path, value: Number(r.count || 0) }))}
            valueKey="value"
            labelKey="label"
            colorClass="bg-indigo-500"
          />
        </PanelCard>

        <PanelCard title="Favori Zekasi" meta={data.favoritesIntel?.meta} emptyText="Favori zekasi verisi yok" help={HELP_BY_PANEL.favorites}>
          <div className="space-y-3">
            <div className="rounded border border-slate-700 bg-slate-900/50 px-2 py-1 text-[11px] text-slate-400">
              Yorum etiketi: orneklem bagimli (kulturel/genel cikarsimlar tek basina gercek kabul edilmez).
            </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Favori Benimseme</p>
                  <p className="text-lg font-black text-cyan-300">
                    {Number(data.favoritesIntel?.favorites?.summary?.favoriteAdoptionRate || 0).toLocaleString('tr-TR')}%
                  </p>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Sik Kullanim Benimseme</p>
                  <p className="text-lg font-black text-indigo-300">
                    {Number(data.favoritesIntel?.favorites?.summary?.frequentAdoptionRate || 0).toLocaleString('tr-TR')}%
                  </p>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Favori -> Kullanim Farki</p>
                  <p className="text-lg font-black text-rose-300">
                    {Number(data.favoritesIntel?.favorites?.summary?.favoriteWithoutObservedRate || 0).toLocaleString('tr-TR')}%
                  </p>
                </div>
            </div>
            {favoriteMetrics ? (
              <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                <p className="mb-2 text-xs font-bold text-slate-300">Haftalik Inceleme Etki Analizi (30g)</p>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="rounded border border-slate-700 bg-slate-950/40 p-2">
                    <p className="text-slate-400">Kuyruk (olusan/cozulen/fark)</p>
                    <p className="font-semibold text-white">
                      {Number(favoriteMetrics.weekly_review_queue_created_count || 0).toLocaleString('tr-TR')} / {Number(favoriteMetrics.weekly_review_queue_resolved_count || 0).toLocaleString('tr-TR')} / {Number(favoriteMetrics.weekly_review_queue_delta || 0).toLocaleString('tr-TR')}
                    </p>
                  </div>
                  <div className="rounded border border-slate-700 bg-slate-950/40 p-2">
                    <p className="text-slate-400">Onay / Red / Duzenleme</p>
                    <p className="font-semibold text-white">
                      {Number(favoriteMetrics.weekly_review_approve_count || 0).toLocaleString('tr-TR')} / {Number(favoriteMetrics.weekly_review_reject_count || 0).toLocaleString('tr-TR')} / {Number(favoriteMetrics.weekly_review_edit_count || 0).toLocaleString('tr-TR')}
                    </p>
                  </div>
                  <div className="rounded border border-slate-700 bg-slate-950/40 p-2">
                    <p className="text-slate-400">Onay / Red Orani</p>
                    <p className="font-semibold text-white">
                      %{Number((Number(favoriteMetrics.weekly_review_approval_rate || 0) * 100)).toLocaleString('tr-TR')} / %{Number((Number(favoriteMetrics.weekly_review_reject_rate || 0) * 100)).toLocaleString('tr-TR')}
                    </p>
                  </div>
                  <div className="rounded border border-slate-700 bg-slate-950/40 p-2">
                    <p className="text-slate-400">Gurultu Azaltma Orani</p>
                    <p className="font-semibold text-amber-300">
                      %{Number((Number(favoriteMetrics.weekly_review_noise_reduction_rate || 0) * 100)).toLocaleString('tr-TR')}
                    </p>
                  </div>
                </div>
                <div className="mt-2 rounded border border-slate-700 bg-slate-950/40 p-2 text-xs">
                  <p className="text-slate-400">Score Korelasyonu (approved vs rejected)</p>
                  <p className="font-semibold text-cyan-300">
                    {Number(favoriteMetrics.weekly_review_score_correlation?.approved?.avgScore || 0).toLocaleString('tr-TR')} / {Number(favoriteMetrics.weekly_review_score_correlation?.rejected?.avgScore || 0).toLocaleString('tr-TR')}
                    {' '}| gap: {Number(favoriteMetrics.weekly_review_score_correlation?.scoreGap || 0).toLocaleString('tr-TR')}
                  </p>
                </div>
              </div>
            ) : null}
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <div>
                <p className="mb-2 text-xs font-bold text-slate-300">En Fazla Favorilenen Tarifler</p>
                <HorizontalBars
                  items={favoriteTopRows.slice(0, 6).map((r) => ({ label: r.title, value: Number(r.favoriteUsers || 0) }))}
                  valueKey="value"
                  labelKey="label"
                  colorClass="bg-cyan-500"
                />
              </div>
              <div>
                <p className="mb-2 text-xs font-bold text-slate-300">En Sık Kullanılan Tarifler</p>
                <HorizontalBars
                  items={frequentTopRows.slice(0, 6).map((r) => ({ label: r.title, value: Number(r.observedTotal || 0) }))}
                  valueKey="value"
                  labelKey="label"
                  colorClass="bg-indigo-500"
                />
              </div>
            </div>
            {favoritesSegmentBreakdown ? (
              <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                <p className="mb-2 text-xs font-bold text-slate-300">Segment Kirilimlari</p>
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                  <div>
                    <p className="mb-1 text-[11px] font-semibold text-slate-400">Plan Type</p>
                    <div className="space-y-1">
                      {(favoritesSegmentBreakdown.byPlanType || []).slice(0, 5).map((row) => (
                        <div key={`seg-plan-${row.segment}`} className="flex items-center justify-between text-[11px]">
                          <span className="truncate pr-2 text-slate-200">{row.segment}</span>
                          <span className="text-cyan-300">{Number(row.events || 0).toLocaleString('tr-TR')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="mb-1 text-[11px] font-semibold text-slate-400">Household Type</p>
                    <div className="space-y-1">
                      {(favoritesSegmentBreakdown.byHouseholdType || []).slice(0, 5).map((row) => (
                        <div key={`seg-household-${row.segment}`} className="flex items-center justify-between text-[11px]">
                          <span className="truncate pr-2 text-slate-200">{row.segment}</span>
                          <span className="text-indigo-300">{Number(row.events || 0).toLocaleString('tr-TR')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="mb-1 text-[11px] font-semibold text-slate-400">Kaynak</p>
                    <div className="space-y-1">
                      {(favoritesSegmentBreakdown.bySource || []).slice(0, 5).map((row) => (
                        <div key={`seg-source-${row.segment}`} className="flex items-center justify-between text-[11px]">
                          <span className="truncate pr-2 text-slate-200">{row.segment}</span>
                          <span className="text-emerald-300">{Number(row.events || 0).toLocaleString('tr-TR')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            {favoritesAnalysisOutputs ? (
              <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                <p className="mb-2 text-xs font-bold text-slate-300">Analiz Ciktilari</p>
                <div className="mb-2 grid grid-cols-2 gap-2">
                  <div className="rounded border border-slate-700 bg-slate-950/40 p-2">
                    <p className="text-[11px] text-slate-400">Beyan / Gozlem</p>
                    <p className="text-xs font-semibold text-white">
                      {Number(favoritesAnalysisOutputs.declaredVsObserved?.declaredPreferenceCount || 0).toLocaleString('tr-TR')} / {Number(favoritesAnalysisOutputs.declaredVsObserved?.observedPreferenceCount || 0).toLocaleString('tr-TR')}
                    </p>
                  </div>
                  <div className="rounded border border-slate-700 bg-slate-950/40 p-2">
                    <p className="text-[11px] text-slate-400">Gozlenen Egilim</p>
                    <p className="text-xs font-semibold text-cyan-300">{trendEtiketi(favoritesAnalysisOutputs.trend?.direction || 'stable')}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <div>
                    <p className="mb-1 text-[11px] font-semibold text-slate-400">Liked ama kullanilmayanlar</p>
                    <div className="space-y-1">
                      {(favoritesAnalysisOutputs.topLikedButNotUsed || []).slice(0, 4).map((row) => (
                        <div key={`an-liked-gap-${row.entityId}`} className="flex items-center justify-between text-[11px]">
                          <span className="truncate pr-2 text-slate-200">{row.title}</span>
                          <span className="text-rose-300">%{Number(row.conversionRate || 0).toLocaleString('tr-TR')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="mb-1 text-[11px] font-semibold text-slate-400">Gozlenen ama begenilmeyen</p>
                    <div className="space-y-1">
                      {(favoritesAnalysisOutputs.topObservedButNotLiked || []).slice(0, 4).map((row) => (
                        <div key={`an-observed-only-${row.entityId}`} className="flex items-center justify-between text-[11px]">
                          <span className="truncate pr-2 text-slate-200">{row.title}</span>
                          <span className="text-emerald-300">{Number(row.observedTotal || 0).toLocaleString('tr-TR')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
              <p className="mb-2 text-xs font-bold text-slate-300">Conversion Gap (Favori var, kullanim dusuk)</p>
              {conversionGapRows.length === 0 ? (
                <p className="text-xs text-slate-400">Gap datası yok.</p>
              ) : (
                <div className="space-y-1">
                  {conversionGapRows.slice(0, 5).map((row) => (
                    <div key={`gap-${row.entityId}`} className="flex items-center justify-between text-xs">
                      <span className="truncate pr-2 text-slate-200">{row.title}</span>
                      <span className="text-rose-300">%{Number(row.conversionRate || 0).toLocaleString('tr-TR')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
              <p className="mb-2 text-xs font-bold text-slate-300">Explainability (Neden bu sirada?)</p>
              {explainRows.length === 0 ? (
                <p className="text-xs text-slate-400">Explainability verisi yok.</p>
              ) : (
                <div className="space-y-2">
                  {explainRows.slice(0, 4).map((row) => (
                    <div key={`exp-${row.entityId}`} className="rounded border border-slate-700 bg-slate-950/40 p-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-xs font-bold text-slate-200">{row.title}</span>
                        <span className="text-[11px] text-cyan-300">score: {Number(row.score || 0).toLocaleString('tr-TR')}</span>
                      </div>
                      <p className="mt-1 text-[11px] text-slate-400">{row.explanationSummary}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </PanelCard>

        <PanelCard title="Malzeme Egilimi" meta={data.ingredientAffinity?.meta} emptyText="Malzeme egilimi verisi yok" help={HELP_BY_PANEL.ingredient}>
          <div className="space-y-3">
            <div className="rounded border border-slate-700 bg-slate-900/50 px-2 py-1 text-[11px] text-slate-400">
              Yorum etiketi: orneklem bagimli (staple filtreleme + sample guard ile okunmalidir).
            </div>
            {ingredientHardRules?.publishBlocked ? (
              <div className="rounded border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
                Hard rule: staple bypass algilandi (`{Number(ingredientHardRules?.stapleBypassDetectedCount || 0)}`), affinity publish bloklandi.
              </div>
            ) : null}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                <p className="text-xs text-slate-400">Tercih Yogunluk Orani</p>
                <p className="text-lg font-black text-emerald-300">
                  {Number(ingredientBreakdown?.preferenceRichRate || 0).toLocaleString('tr-TR')}%
                </p>
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                <p className="text-xs text-slate-400">Temel Malzeme Orani</p>
                <p className="text-lg font-black text-amber-300">
                  {Number(ingredientBreakdown?.stapleRate || 0).toLocaleString('tr-TR')}%
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                <p className="text-xs text-slate-400">Tercih Kapsama ({String(preferenceCoverage?.window || filters.window)})</p>
                <p className="text-lg font-black text-cyan-300">
                  {Number(preferenceCoverage?.rate || 0).toLocaleString('tr-TR')}%
                </p>
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                <p className="text-xs text-slate-400">Trend 7g</p>
                <p className="text-lg font-black text-indigo-300">
                  {Number(preferenceCoverage?.trend7d?.rate || 0).toLocaleString('tr-TR')}%
                </p>
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                <p className="text-xs text-slate-400">Trend 30g</p>
                <p className="text-lg font-black text-emerald-300">
                  {Number(preferenceCoverage?.trend30d?.rate || 0).toLocaleString('tr-TR')}%
                </p>
              </div>
            </div>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <div>
                <p className="mb-2 text-xs font-bold text-slate-300">En Cok Kullanilan Malzemeler</p>
                <HorizontalBars
                  items={topUsedIngredientRows.slice(0, 8).map((r) => ({ label: r.ingredientName, value: Number(r.occurrence || 0) }))}
                  valueKey="value"
                  labelKey="label"
                  colorClass="bg-amber-500"
                />
              </div>
              <div>
                <p className="mb-2 text-xs font-bold text-slate-300">En Guclu Egilim Malzemeleri</p>
                <HorizontalBars
                  items={topAffinityIngredientRows.slice(0, 8).map((r) => ({ label: r.ingredientName, value: Number(r.affinityScore || 0) }))}
                  valueKey="value"
                  labelKey="label"
                  colorClass="bg-emerald-500"
                />
              </div>
            </div>
          </div>
        </PanelCard>

        <PanelCard title="Mutfak Karar Golge" meta={data.insights?.meta} emptyText="Golge metrik henuz yok" help={HELP_BY_PANEL.shadow}>
          {!kitchenShadowMetrics ? (
            <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-3 text-sm text-slate-400">
              Shadow metrikleri henuz toplanmamis veya feature kapali.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-slate-400">Zaman Penceresi</p>
                <select
                  value={shadowDays}
                  onChange={(e) => setShadowDays(Number(e.target.value || 30))}
                  className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-200"
                >
                  <option value={7}>7 gun</option>
                  <option value={30}>30 gun</option>
                  <option value={60}>60 gun</option>
                </select>
              </div>
              <div className={`rounded border px-2 py-1 text-[11px] ${kitchenShadowHealth?.shadowParityStatus === 'PASS' ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200' : 'border-rose-500/40 bg-rose-500/10 text-rose-200'}`}>
                GOLGE KILIDI: {kitchenShadowHealth?.shadowLockActive ? 'AKTIF' : 'PASIF'} | etki=0 eslik: {String(kitchenShadowHealth?.shadowParityStatus || 'BILINMIYOR')}
              </div>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Hesaplanan Profil</p>
                  <p className="text-lg font-black text-white">{Number(kitchenShadowMetrics.decision_profile_computed_count || 0).toLocaleString('tr-TR')}</p>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Ortalama Guven</p>
                  <p className="text-lg font-black text-cyan-300">{Number(kitchenShadowMetrics.decision_profile_confidence_avg || 0).toLocaleString('tr-TR')}</p>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Mod Dagilimi</p>
                  <p className="text-xs font-semibold text-slate-200">
                    {Object.entries(kitchenShadowMetrics.decision_profile_mode_distribution || {}).map(([k, v]) => `${k}:${v}`).join(' | ') || '-'}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Maksimum Etki</p>
                  <p className="text-lg font-black text-amber-300">{Number(kitchenShadowHealth?.maxInfluenceFactor || 0).toLocaleString('tr-TR')}</p>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Maksimum Delta</p>
                  <p className="text-lg font-black text-indigo-300">%{Number((Number(kitchenShadowHealth?.maxDeltaPercent || 0) * 100)).toLocaleString('tr-TR')}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Warmup Adayi Kullanici</p>
                  <p className="text-lg font-black text-emerald-300">{Number(kitchenShadowMetrics.warmup_eligible_user_count || 0).toLocaleString('tr-TR')}</p>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Low Sample Kullanici</p>
                  <p className="text-lg font-black text-amber-300">{Number(kitchenShadowMetrics.low_sample_user_count || 0).toLocaleString('tr-TR')}</p>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Son Hesaplama</p>
                  <p className="text-xs font-semibold text-slate-200">
                    {kitchenShadowHealth?.lastComputedAt ? new Date(kitchenShadowHealth.lastComputedAt).toLocaleString('tr-TR') : '-'}
                  </p>
                </div>
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                <p className="text-xs text-slate-400">Shadow Veri Kapsami</p>
                <p className="text-lg font-black text-cyan-300">{Number(kitchenShadowMetrics.shadow_data_days_covered || 0).toLocaleString('tr-TR')} gun</p>
              </div>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Top1 Ortusme</p>
                  <p className="text-lg font-black text-cyan-300">{Number(kitchenShadowMetrics.profile_vs_base_top1_overlap_rate || 0).toLocaleString('tr-TR')}%</p>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Profil Top3 Isabet</p>
                  <p className="text-lg font-black text-emerald-300">{Number(kitchenShadowMetrics.profile_top3_hit_rate || 0).toLocaleString('tr-TR')}%</p>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Siralama Kaymasi P95</p>
                  <p className="text-lg font-black text-amber-300">{Number(kitchenShadowMetrics.profile_rank_shift_p95 || 0).toLocaleString('tr-TR')}</p>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Gerekce Tutarliligi</p>
                  <p className="text-lg font-black text-indigo-300">{Number(kitchenShadowMetrics.profile_reason_coherence_rate || 0).toLocaleString('tr-TR')}%</p>
                </div>
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                <p className="text-xs text-slate-400">Siralama Sicrama Engeli</p>
                <p className="text-lg font-black text-amber-300">{Number(kitchenShadowMetrics.rank_jump_block_count || 0).toLocaleString('tr-TR')}</p>
              </div>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Guven Dagilimi</p>
                  <p className="mt-1 text-xs text-slate-300">
                    low: <span className="font-bold text-rose-300">{Number(kitchenShadowMetrics.profile_confidence_distribution?.low || 0)}</span> | medium: <span className="font-bold text-amber-300">{Number(kitchenShadowMetrics.profile_confidence_distribution?.medium || 0)}</span> | high: <span className="font-bold text-emerald-300">{Number(kitchenShadowMetrics.profile_confidence_distribution?.high || 0)}</span>
                  </p>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Dusuk Guven Engeli</p>
                  <p className="text-lg font-black text-rose-300">{Number(kitchenShadowMetrics.profile_low_confidence_block_count || 0).toLocaleString('tr-TR')}</p>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Kayma Uyarilari</p>
                  <p className="text-lg font-black text-amber-300">{Number(kitchenShadowMetrics.profile_drift_alert_count || 0).toLocaleString('tr-TR')}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="mb-2 text-xs font-bold text-slate-300">Dusuk Confidence Kullanici Ornekleri</p>
                  {!Array.isArray(kitchenShadowDiagnostics?.lowConfidenceUsers) || kitchenShadowDiagnostics.lowConfidenceUsers.length === 0 ? (
                    <p className="text-xs text-slate-400">Ornek yok.</p>
                  ) : (
                    <div className="space-y-1">
                      {kitchenShadowDiagnostics.lowConfidenceUsers.slice(0, 8).map((r) => (
                        <div key={`low-conf-${r.userId}`} className="flex items-center justify-between text-[11px]">
                          <span className="text-slate-200">u#{r.userId}</span>
                          <span className="text-rose-300">c={Number(r.confidence || 0).toLocaleString('tr-TR')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="mb-2 text-xs font-bold text-slate-300">Base vs Profile Top1 Degisenler</p>
                  {!Array.isArray(kitchenShadowDiagnostics?.changedTop1Users) || kitchenShadowDiagnostics.changedTop1Users.length === 0 ? (
                    <p className="text-xs text-slate-400">Degisen top1 kaydi yok.</p>
                  ) : (
                    <div className="space-y-1">
                      {kitchenShadowDiagnostics.changedTop1Users.slice(0, 8).map((r, i) => (
                        <div key={`chg-top1-${r.userId}-${i}`} className="text-[11px] text-slate-200">
                          <span className="text-slate-300">u#{r.userId}</span> <span className="text-amber-300">{r.baseTop1 || '-'}</span> -> <span className="text-cyan-300">{r.profileTop1 || '-'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                <p className="mb-2 text-xs font-bold text-slate-300">Son Explanation Ornekleri</p>
                {!Array.isArray(kitchenShadowDiagnostics?.recentExplanationLogs) || kitchenShadowDiagnostics.recentExplanationLogs.length === 0 ? (
                  <p className="text-xs text-slate-400">Explanation log yok.</p>
                ) : (
                  <div className="space-y-2">
                    {kitchenShadowDiagnostics.recentExplanationLogs.slice(0, 20).map((r, i) => (
                      <div key={`exp-log-${r.userId}-${i}`} className="rounded border border-slate-700 bg-slate-950/40 p-2 text-[11px]">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-slate-200">u#{r.userId} - {r.title || r.recipeId}</span>
                          <span className="text-cyan-300">c={Number(r.confidence || 0).toLocaleString('tr-TR')}</span>
                        </div>
                        <p className="mt-1 text-slate-400">{Array.isArray(r.reasons) && r.reasons.length ? r.reasons.join(' | ') : 'reason yok'}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </PanelCard>

        <PanelCard title="Negatif Sinyaller" meta={data.negatives?.meta} emptyText="Negatif sinyal verisi yok" help={HELP_BY_PANEL.negatives}>
          <div className="space-y-3">
            {[{ label: 'Aksiyon Yok', rate: noActionRate }, { label: 'Ofkeli Tiklama', rate: rageRate }, { label: 'Hizli Geri Donus', rate: rapidRate }, { label: 'Tekrar Arama', rate: repeatRate }].map((s) => (
              <div key={s.label}>
                <div className="mb-1 flex items-center justify-between text-xs text-slate-300">
                  <span>{s.label}</span>
                  <span>{s.rate.toLocaleString('tr-TR')}%</span>
                </div>
                <div className="h-2 rounded-full bg-slate-800"><div className="h-2 rounded-full bg-rose-500" style={{ width: `${Math.min(100, s.rate)}%` }} /></div>
              </div>
            ))}
          </div>
        </PanelCard>

        <PanelCard title="Canli Mini" meta={data.realtime?.meta} emptyText="Canli veri yok" help={HELP_BY_PANEL.realtime}>
          {realtime ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Aktif Kullanici</p>
                  <p className="text-lg font-black text-emerald-300">{realtime.activeUsers || 0}</p>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <p className="text-xs text-slate-400">Aktif Oturum</p>
                  <p className="text-lg font-black text-emerald-300">{realtime.activeSessions || 0}</p>
                </div>
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                <p className="mb-2 text-xs text-slate-400">Son 5 Olay</p>
                <ul className="space-y-1 text-sm">
                  {(realtime.lastEvents || []).length === 0 ? (
                    <li className="text-slate-400">Yakin zamanda olay yok</li>
                  ) : (realtime.lastEvents || []).map((e) => (
                    <li key={e.id} className="flex items-center justify-between text-slate-200">
                      <span>{e.event}</span>
                      <span className="text-xs text-slate-500">{new Date(e.createdAt).toLocaleTimeString('tr-TR')}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
        </PanelCard>

        <PanelCard title="Otomatik Icgoruler" meta={data.insights?.meta} emptyText="Icgoru henuz yok" help={HELP_BY_PANEL.insights}>
          <div className="space-y-2">
            <div className="rounded border border-slate-700 bg-slate-900/40 px-2 py-1 text-[11px] text-slate-400">
              Preference false-positive (30d): {Number(preferenceFeedbackSummary?.falsePositiveRate || 0).toLocaleString('tr-TR')}% | feedback: {Number(preferenceFeedbackSummary?.feedbackCount || preferenceFeedbackSummary?.totalCount || 0).toLocaleString('tr-TR')}
            </div>
            {insights.length === 0 ? (
              <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-3 text-sm text-slate-400">
                Henuz yeterli veri yok. Veri toplanmaya devam ediyor...
              </div>
            ) : insights.map((insight) => (
              <div key={insight.key} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <p className="text-sm font-bold text-white">{insight.title}</p>
                  <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${insight.severity === 'ACTION_REQUIRED' ? 'bg-rose-500/20 text-rose-300' : 'bg-slate-700 text-slate-200'}`}>
                    {insight.severity}
                  </span>
                </div>
                <p className="text-xs text-slate-300">{insight.detail}</p>
                <p className="mt-1 text-[11px] text-slate-500">
                  Confidence: {insight.confidence} | n={insight.sampleSize}
                </p>
                {String(insight?.key || '').includes('preference') || String(insight?.key || '').includes('false_favorite') ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      onClick={() => submitPreferenceInsightFeedback(insight, true)}
                      disabled={preferenceFeedbackBusy === String(insight?.key || '')}
                      className="rounded border border-emerald-700 px-2 py-1 text-[11px] text-emerald-300 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Faydali
                    </button>
                    <button
                      onClick={() => submitPreferenceInsightFeedback(insight, false)}
                      disabled={preferenceFeedbackBusy === String(insight?.key || '')}
                      className="rounded border border-rose-700 px-2 py-1 text-[11px] text-rose-300 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Faydali Degil
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
            {preferenceFeedbackError ? (
              <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-2 text-xs text-rose-200">
                Preference insight feedback hatasi: {preferenceFeedbackError}
              </div>
            ) : null}
          </div>
        </PanelCard>

        {!aiNarrativeForbidden ? <PanelCard title="YZ Anlatimi" meta={aiNarrative?.meta || data.insights?.meta} emptyText="YZ anlatimi henuz yok" help={HELP_BY_PANEL.aiNarrative}>
          {aiNarrativeDisabled ? (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
              YZ Anlatimi kapali. `Admin / Config` ekranindan `ADMIN_ANALYTICS_AI_NARRATIVE_ENABLED=true` yapilmadan bu panel konusmaz.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full border border-cyan-500/40 bg-cyan-500/10 px-2 py-1 text-[10px] font-bold text-cyan-200">YZ Onizleme</span>
                <span className="rounded-full border border-indigo-500/40 bg-indigo-500/10 px-2 py-1 text-[10px] font-bold text-indigo-200">Karar Destegi</span>
                <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[10px] font-bold text-amber-200">Insan Onayi Gerekli</span>
              </div>
              {aiNarratives.length === 0 ? (
                <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-3 text-sm text-slate-400">
                  Henuz yeterli veri yok. Veri toplanmaya devam ediyor...
                </div>
              ) : aiNarratives.map((n) => {
                const lowSample = Boolean(n?.guardrails?.lowSample);
                const status = n?.actionItem?.status || null;
                const busy = aiActionBusyTopic === n.topic;
                return (
                  <div key={n.topic} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                    <div className="mb-1 flex items-center justify-between">
                      <p className="text-sm font-bold text-white">{n.topic}</p>
                      <span className="text-[11px] text-slate-400">n={Number(n.sampleSize || 0)} | {String(n.timeWindow || 'default')}</span>
                    </div>
                    <p className="text-xs text-slate-200"><span className="font-bold">Ne oldu:</span> {n.whatHappened}</p>
                    <p className="mt-1 text-xs text-slate-300"><span className="font-bold">Neden:</span> {n.whyLikely}</p>
                    <p className="mt-1 text-xs text-cyan-300"><span className="font-bold">Ne yapmali:</span> {n.recommendedAction || 'Dusuk orneklem nedeniyle aksiyon onerisi yok.'}</p>
                    <p className="mt-1 text-[11px] text-slate-500">confidence: {String(n.confidence || 'low')} | impact: {String(n.impact || 'medium')}</p>
                    {n?.metricParity?.ok === false ? (
                      <div className="mt-1 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-200">
                        Metrik tutarlilik uyarisi: {Array.isArray(n.metricParity.missingEvidenceKeys) ? n.metricParity.missingEvidenceKeys.join(', ') : 'bilinmiyor'}
                      </div>
                    ) : null}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {aiCanCreateActionItems ? (
                        <>
                          <button
                            onClick={() => submitNarrativeAction(n, 'open')}
                            disabled={busy || lowSample || !n.recommendedAction}
                            className="rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {busy ? 'Isleniyor...' : 'Aksiyona Donustur'}
                          </button>
                          <button
                            onClick={() => submitNarrativeAction(n, 'dismissed')}
                            disabled={busy || lowSample}
                            className="rounded-md border border-slate-700 px-3 py-1.5 text-xs font-bold text-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Yok Say
                          </button>
                        </>
                      ) : (
                        <span className="rounded border border-slate-700 bg-slate-800 px-2 py-1 text-[11px] text-slate-300">
                          Sadece goruntuleme izni
                        </span>
                      )}
                      {status ? (
                        <span className="rounded border border-slate-700 bg-slate-800 px-2 py-1 text-[11px] text-slate-200">durum: {aksiyonDurumEtiketi(status)}</span>
                      ) : null}
                      <button
                        onClick={() => submitNarrativeFeedback(n, true)}
                        disabled={narrativeFeedbackBusy === (n.insightId || n.topic)}
                        className="rounded border border-emerald-700 px-2 py-1 text-[11px] text-emerald-300 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Faydali
                      </button>
                      <button
                        onClick={() => submitNarrativeFeedback(n, false)}
                        disabled={narrativeFeedbackBusy === (n.insightId || n.topic)}
                        className="rounded border border-rose-700 px-2 py-1 text-[11px] text-rose-300 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Faydali Degil
                      </button>
                    </div>
                  </div>
                );
              })}
              {aiActionError ? (
                <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-2 text-xs text-rose-200">
                  Aksiyon islemi basarisiz: {aiActionError}
                </div>
              ) : null}
              {narrativeFeedbackError ? (
                <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-2 text-xs text-rose-200">
                  Anlatim geri bildirimi hatasi: {narrativeFeedbackError}
                </div>
              ) : null}
            </div>
          )}
        </PanelCard> : null}

        {!aiNarrativeForbidden ? (
          <PanelCard title="Aksiyon Sirasi" meta={aiNarrative?.meta || data.insights?.meta} emptyText="Aksiyon ogesi yok" help={HELP_BY_PANEL.actionQueue}>
            {actionQueueLoading ? (
              <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-3 text-sm text-slate-400">Yukleniyor...</div>
            ) : (
              <div className="space-y-2">
                {actionQueue.length === 0 ? (
                  <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-3 text-sm text-slate-400">Aksiyon ogesi henuz yok.</div>
                ) : actionQueue.slice(0, 20).map((item) => (
                  <div key={item.id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <p className="text-sm font-bold text-white">{item.title}</p>
                      <span className="rounded border border-slate-700 bg-slate-800 px-2 py-0.5 text-[10px] text-slate-200">{aksiyonDurumEtiketi(item.status)}</span>
                    </div>
                    <p className="text-xs text-slate-300">{item.topic} | yas: {formatAge(item.createdAt)} | sorumlu: {item.ownerUserId || '-'}</p>
                    <p className="mt-1 text-[11px] text-slate-500">guven: {item.confidence} | etki: {item.impact}</p>
                    {aiCanCreateActionItems ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button onClick={() => patchActionItemStatus(item.id, 'acknowledged')} className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-200">Onaylandi</button>
                        <button onClick={() => patchActionItemStatus(item.id, 'in_progress')} className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-200">Suruyor</button>
                        <button onClick={() => patchActionItemStatus(item.id, 'resolved')} className="rounded border border-emerald-700 px-2 py-1 text-[11px] text-emerald-300">Cozuldu</button>
                        <button onClick={() => patchActionItemStatus(item.id, 'dismissed')} className="rounded border border-rose-700 px-2 py-1 text-[11px] text-rose-300">Kapat</button>
                      </div>
                    ) : null}
                  </div>
                ))}
                {actionQueueError ? (
                  <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-2 text-xs text-rose-200">
                    Aksiyon Sirasi hatasi: {actionQueueError}
                  </div>
                ) : null}
              </div>
            )}
          </PanelCard>
        ) : null}
      </div>
    </div>
  );
}

